import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import CloudIcon from '../images/Icon20_RevisionCloud.svg';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/fabric_overlay';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import { getThicknessPx } from '../helpers/fabricHelpers';
import { __ } from '../../../core/utils';

const segmentSize = 50;

const getCloudPathFromSize = (width, height) => {
  const path = [];

  const addCloudSegment = (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const offset = segmentSize / 2;

    const cp1 = {
      x: p1.x + ny * offset,
      y: p1.y - nx * offset,
    };
    const cp2 = {
      x: p2.x + ny * offset,
      y: p2.y - nx * offset,
    };

    path.push(['C', cp1.x, cp1.y, cp2.x, cp2.y, p2.x, p2.y]);
  };

  const segmentsX = Math.max(1, Math.floor(width / segmentSize));
  const segmentsY = Math.max(1, Math.floor(height / segmentSize));

  const stepX = width / segmentsX;
  const stepY = height / segmentsY;

  path.push(['M', 0, 0]);

  for (let i = 0; i < segmentsX; i++) {
    const p1 = { x: i * stepX, y: 0 };
    const p2 = { x: (i + 1) * stepX, y: 0 };
    addCloudSegment(p1, p2);
  }

  for (let i = 0; i < segmentsY; i++) {
    const p1 = { x: width, y: i * stepY };
    const p2 = { x: width, y: (i + 1) * stepY };
    addCloudSegment(p1, p2);
  }

  // Bottom
  for (let i = segmentsX; i > 0; i--) {
    const p1 = { x: i * stepX, y: height };
    const p2 = { x: (i - 1) * stepX, y: height };
    addCloudSegment(p1, p2);
  }

  // Left
  for (let i = segmentsY; i > 0; i--) {
    const p1 = { x: 0, y: i * stepY };
    const p2 = { x: 0, y: (i - 1) * stepY };
    addCloudSegment(p1, p2);
  }

  path.push(['Z']);
  return path;
};

const pathArrayToString = (pathArray) => {
  return pathArray.map((cmd) => cmd.join(' ')).join(' ');
};

const Cloud = ({
  fabric_overlay: {
    activeTool,
    viewer,
    fabricOverlay,
    color,
    selectedThickness,
  },
  isActive,
  plan,
  user_id,
  user_full_name,
}) => {
  const [state, setState] = useState({
    path: null,
    isMouseDown: false,
    origX: null,
    origY: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isActive || !fabricOverlay) return;

    const canvas = fabricOverlay.fabricCanvas();
    canvas.defaultCursor = 'crosshair';
    canvas.selection = false;

    setCursorTooltip(__('canvas.tooltip.shape.start'));

    canvas.getObjects().forEach((obj) => {
      obj.selectable = false;
      obj.evented = false;
    });

    return () => {
      canvas.discardActiveObject();
      //canvas.requestRenderAll();
    };
  }, [isActive, fabricOverlay]);

  useEffect(() => {
    if (!isActive || !fabricOverlay) return;
    const canvas = fabricOverlay.fabricCanvas();

    const onMouseDown = (options) => {
      if (options.target || !isActive) return;

      const rawPointer = canvas.getPointer(options.e);
      const pointer = {
        x: Math.max(0, Math.min(plan.width, rawPointer.x)),
        y: Math.max(0, Math.min(plan.height, rawPointer.y)),
      };

      const origX = pointer.x;
      const origY = pointer.y;

      const path = new fabric.Path(
        getCloudPathFromSize(segmentSize, segmentSize),
        {
          left: origX - segmentSize / 2.5,
          top: origY - segmentSize / 2.5,
          stroke: color.hex,
          strokeWidth: getThicknessPx(selectedThickness.key),
          fill: 'transparent',
          objectType: 'cloud',
          selectable: true,
          hasControls: false,
          hasBorders: false,
          evented: true,
          strokeUniform: true,
          userID: user_id,
          userFullName: user_full_name,
        }
      );

      canvas.add(path);
      //canvas.setActiveObject(path);

      setState({
        path,
        isMouseDown: true,
        origX,
        origY,
      });
    };

    const onMouseMove = (options) => {
      if (!isActive || !stateRef.current.isMouseDown || !stateRef.current.path)
        return;

      const rawPointer = canvas.getPointer(options.e);

      const pointer = {
        x: Math.max(0, Math.min(plan.width, rawPointer.x)),
        y: Math.max(0, Math.min(plan.height, rawPointer.y)),
      };

      const startX = stateRef.current.origX;
      const startY = stateRef.current.origY;

      const deltaX = pointer.x - startX;
      const deltaY = pointer.y - startY;

      const width = Math.abs(deltaX);
      const height = Math.abs(deltaY);

      let left =
        deltaX < 0
          ? startX + deltaX - segmentSize / 2.5
          : startX - segmentSize / 2.5;
      let top =
        deltaY < 0
          ? startY + deltaY - segmentSize / 2.5
          : startY - segmentSize / 2.5;

      if (left < 0) left = 0;
      if (top < 0) top = 0;

      canvas.remove(stateRef.current.path);

      const cloudPathArray = getCloudPathFromSize(width || 1, height || 1);
      const cloudPathString = pathArrayToString(cloudPathArray);

      const newPath = new fabric.Path(cloudPathString, {
        left,
        top,
        stroke: color.hex,
        strokeWidth: getThicknessPx(selectedThickness.key),
        fill: 'transparent',
        selectable: false,
        evented: false,
        strokeUniform: true,
        objectType: 'cloud',
        userID: user_id,
        userFullName: user_full_name,
      });

      canvas.add(newPath);
      stateRef.current.path = newPath;

      newPath.setCoords();
      canvas.requestRenderAll();

      setCursorTooltip(__('canvas.tooltip.shape.drawing'));
    };

    const onMouseUp = () => {
      if (!isActive || !stateRef.current.path) return;

      viewer.setMouseNavEnabled(true);

      setState({
        path: null,
        isMouseDown: false,
        origX: null,
        origY: null,
      });

      updateTool({ tool: 'hand' });

      setCursorTooltip(__('canvas.tooltip.shape.start'));
    };

    const onPointerDown = () => {
      viewer.setMouseNavEnabled(false);
    };

    canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);

    return () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
      canvas.upperCanvasEl.removeEventListener('pointerdown', onPointerDown);
    };
  }, [fabricOverlay, isActive, color, selectedThickness]);

  const toggleTool = () => {
    updateTool({ tool: 'cloud' });
  };

  return (
    <Button
      id='cloud'
      label={__('canvas.toolbar.cloud')}
      icon={CloudIcon}
      active={activeTool === 'cloud'}
      onClick={toggleTool}
      extraClass={['withSpacer']}
    />
  );
};

Cloud.propTypes = {
  isActive: PropTypes.bool.isRequired,
  plan: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  fabric_overlay: state.fabric_overlay,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Cloud);
